import PaymentStepsAccordion from "@components/integration/PaymentStepsAccordian";
import PaymentStepsCard from "@components/integration/PaymentStepsCard";
import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTAButtons from "@components/new-ui/CTAButtons";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import Image from "next/image";
import { GoArrowUpRight } from "react-icons/go";

interface PaymentGateWay {
    icon: string;
    title: string;
    descritpion: string;
    externalLink: string;
}

const paymentGateWayData: PaymentGateWay[] = [
    {
        icon: "https://assets.gallabox.com/gb-home/integration/razor-pay.svg",
        title: "Razorpay",
        descritpion: "Enhance your business transactions with RazorPay payment integration!",
        externalLink: "https://gallabox.com/whatsapp-integrations/razorpay",
    },
    {
        icon: "https://assets.gallabox.com/gb-home/integration/strip.svg",
        title: "Stripe",
        descritpion: "Collect payments directly through WhatsApp with CashFree integration!",
        externalLink: "https://docs.gallabox.com/integration/payment-gateways/stripe/stripe-workflows",
    },

    {
        icon: "https://assets.gallabox.com/gb-home/integration/cash-free.svg",
        title: "Cashfree",
        descritpion: "Get a single source of truth with a two-way sync between Gallabox and Zoho CRM",
        externalLink: "https://docs.gallabox.com/integration/payment-gateways/cashfree/workflows-for-cashfree",
    },
];

const featureInfo1: FeatureInfo = {
    mainTitle: "Generate payment links",
    imageUrl: "https://assets.gallabox.com/gb-home/integration/payment-link.png",
    featureInfoList: ["Simple, one-click payments", "Trackable, customized payment links", "Full visibility on every transaction"],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Automated payment updates",
    imageUrl: "https://assets.gallabox.com/gb-home/integration/payment-update.png",
    featureInfoList: ["Prompt payment confirmations and reminders", "Instant receipt generation and invoice sharing", "Organized real-time chat updates"],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Personalized broadcasts",
    imageUrl: "https://assets.gallabox.com/gb-home/integration/personalize-broadcast.png",
    featureInfoList: ["Customer segmentation based on user behavior", "Targeted broadcasts and reminders", "Upselling of products/services based on purchase history"],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Simplified payments for service-based businesses",
    imageUrl: "https://assets.gallabox.com/gb-home/integration/travelcation.png",
    featureInfoList: [
        "Fee collection and enrollments done directly via WhatsApp",
        "Appointment confirmations and service quotes sharing",
        "Abandoned cart recovery to encourage conversions",
    ],
};

const productCardData: Array<Product> = [
    {
        title: "Use actionable messages with payment links",
        description: "Add clear nudges to create urgency and encourage users to pay on time.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-call.svg",
        headerBackground: true,
    },
    {
        title: "Automate follow-ups for pending dues or missed payments",
        description: "Set up workflows to send out payment reminders to customers well in advance.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-chat.svg",
        headerBackground: true,
    },
    {
        title: "Segment audience for different campaigns",
        description: "Segregate audiences based on user behavior to send customized messages.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/campaign.svg",
        headerBackground: true,
    },
    {
        title: "Track and optimize performance",
        description: "Identify trends by monitoring links that perform well, and improve messaging.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/dollar.svg",
        headerBackground: true,
    },
];

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/integration/jutti-choo.png",
        chiptitle: "E-commerce",
        title: "How Gallabox Helped JuttiChoo Boost Sales and Enhance Customer Communication",
        statsInfoData: [
            { percentage: "74%", label: "Improving operational efficiency ad conversion rates" },
            { percentage: "82%", label: "Recovering of abandoned carts first-time customers" },
            { percentage: "54%", label: "Reducing cost per acquisition after using Gallabox flow" },
        ],
        url: "https://gallabox.com/case-study/juttichoo",
        cardColor: true,
    },
];

const defaultFAQData: FAQSectionProps = {
    title: "Got questions? Perhaps these FAQs can help!",
    faqItems: [
        {
            question: "What is WhatsApp Payment Integration?",
            answer: (
                <div>
                    WhatsApp payment integration is the method of integrating payment gateways like RazorPay, CashFree, Stripe, PayU, etc., with WhatsApp channels or WhatsApp
                    Business accounts. This is done mainly to streamline payments within WhatsApp, instead of redirecting a ready-to-buy customer to another platform.
                </div>
            ),
        },
        {
            question: "Is it safe to process payments via WhatsApp Payment Integration?",
            answer: (
                <>
                    Yes, processing payments through WhatsApp Payment Integration is secure. Transactions are processed through encrypted, PCI-compliant (Payment Card Industry)
                    gateways, and all chats, including payment links, are end-to-end encrypted. This two-layer security ensures safe transactions.
                </>
            ),
        },
        {
            question: "Can I accept payments from international customers through WhatsApp Payment Integration?",
            answer: (
                <p>
                    Yes, WhatsApp Payment Integration can support international transactions based on your payment gateway. Platforms like Stripe and Razorpay support global
                    currencies, and you can collect payments worldwide. Payment links can be customised for currency, amount, and language.
                </p>
            ),
        },
        {
            question: "Can I offer multiple payment methods (credit card, wallet, etc.) using WhatsApp Payment Integration?",
            answer: (
                <div>
                    Yes! Through Gallabox’s WhatsApp Payment Integration with platforms like RazorPay, you can offer multiple payment methods like credit cards, debit cards, net
                    banking, UPI, digital wallets, etc. Using these integrations, businesses can manage their online payments.
                </div>
            ),
        },
        {
            question: "Does WhatsApp Payment Integration support recurring payments or subscriptions?",
            answer: (
                <div>
                    Yes! Using Gallabox, you can easily set up recurring payments or subscriptions. You can also set up payment reminders using ready-to-use and customizable
                    templates to automate status updates
                </div>
            ),
        },
        {
            question: "Can I automate payment reminders and notifications through WhatsApp Payment Integration?",
            answer: (
                <>
                    Absolutely! With Gallabox, you can automate payment reminders for due or pending invoices via custom WhatsApp messages. Set up workflows for reminders before
                    due dates, nudge for overdue payments and reduce manual follow-ups, and increase collection rates.
                </>
            ),
        },
        {
            question: "Is it possible to send invoices and receipts automatically through WhatsApp Payment Integration?",
            answer: (
                <p>
                    Yes! Gallabox integrates with payment platforms like RazorPay, Cashfree, and Stripe, allowing you to connect your business&apos;s payment processing with
                    WhatsApp. You can create and customize automated workflows to trigger invoice sending and receipt generation based on specific events or triggers.
                </p>
            ),
        },
        {
            question: "What payment gateways can I use with WhatsApp Payment Integration?",
            answer: (
                <p>
                    Gallabox supports payment gateways like RazorPay, Stripe, and Cashfree. If you are using one of these payment gateways, you can readily integrate and use them
                    to send payment links, track the payment status, provide real-time updates, and manage everything through a common dashboard.
                </p>
            ),
        },
        {
            question: "How do I track my WhatsApp payments?",
            answer: (
                <p>
                    With Gallabox, tracking WhatsApp payments is easy and seamless. Use a real-time dashboard to monitor transaction status, generate reports, view payment history
                    per customer, and get automated updates so you have full cash flow visibility without switching tools.
                </p>
            ),
        },
        {
            question: "How to integrate payments in WhatsApp?",
            answer: (
                <div>
                    To integrate payments in WhatsApp, use a platform like Gallabox that connects WhatsApp with your preferred payment gateways. Simply generate custom links, set
                    up workflows, and automate your communication. Once integrated, you can manage transactions, send reminders, and collect payments.
                </div>
            ),
        },
        {
            question: "Can you do payments through WhatsApp?",
            answer: (
                <div>
                    Absolutely! With the help of Gallabox, you can send secure payment links via WhatsApp, allowing customers to pay instantly. You can also automate follow-ups,
                    send receipts and reminders, and track status updates, all from your WhatsApp Business account.
                </div>
            ),
        },
        {
            question: "What is WhatsApp Payment API in India?",
            answer: (
                <div>
                    In India, the WhatsApp Payment API refers to third-party tools that enable UPI-based or gateway-based transactions through WhatsApp. While native WhatsApp UPI
                    exists, business payments need more control, like reminders, invoices, and analytics. This is where platforms like Gallabox step in.
                </div>
            ),
        },
        {
            question: "How do I create a payment link on WhatsApp?",
            answer: (
                <div>
                    You can use Gallabox to generate a trackable payment link in seconds. Just enter the amount, purpose, and customer details, and your link will be ready to send
                    via WhatsApp. These links support multiple payment modes and provide real-time status updates.
                </div>
            ),
        },
        {
            question: "How do I set up WhatsApp integration?",
            answer: (
                <div>
                    Start by getting a WhatsApp Business API account (Gallabox can help set it up). Then connect your number, integrate your payment gateway, and customize
                    workflows like reminders, invoices, and updates. Setup is easy and doesn’t require any coding at all.
                </div>
            ),
        },
        {
            question: "How can I get paid through WhatsApp?",
            answer: (
                <div>
                    Absolutely! With the help of Gallabox, you can send secure payment links via WhatsApp, allowing customers to pay instantly. You can also automate follow-ups,
                    send receipts and reminders, and track status updates, all from your WhatsApp Business account.
                </div>
            ),
        },
    ],
};

const Integration: React.FC = () => {
    return (
        <NewLayout
            title={"WhatsApp Payment Integration for Secure Transactions"}
            description={
                "Integrate WhatsApp payments securely and easily. Accept credit card payments, wallets, and more. Automated and efficient with Gallabox - sign up and try it now!"
            }
            canonical={"https://gallabox.com/integrations/payment/whatsapp"}
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/whatsapp-marketing/wa-integration-og.png" }}
        >
            <section style={{ background: "linear-gradient(265deg, #A1D8FF -7.73%, #396AFF 19.04%, #00238D 61.31%, #000 102.16%" }}>
                <div className={`${container_classname} md:pt-40 pt-20 pb-20 flex md:flex-row flex-col gap-20 text-white`}>
                    <div className="md:w-1/2  w-full">
                        <h1 className="lg:text-7xl text-5xl font-bold mb-5" style={{ fontFamily: "Degular Variable" }}>
                            WhatsApp Payment Integration: Business payments made easy
                        </h1>
                        <p className="mb-8">
                            Payments should be a one-step process. With Gallabox’s WhatsApp Payment Integration, help customers shop, pay, and transact without hassles!
                        </p>
                        <CTAButtons primaryButtonText="Try it now" />
                        <div className="mt-6 flex gap-10">
                            <p>No setup fee.</p>
                            <p>|</p>
                            <p>No credit card required.</p>
                        </div>
                    </div>
                    <Image src={"https://assets.gallabox.com/gb-home/integration/hero-image.png"} alt={""} width={480} height={480} />
                </div>
            </section>

            <section className="mt-20 mb-20">
                <div className="w-full">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>
            </section>
            <section className={`${container_classname} mb-20`}>
                <h3 className="mb-5 md:text-6xl text-4xl">Payment gateways supported by Gallabox</h3>
                <p>Integrate top payment gateways like RazorPay, CashFree, and Stripe effortlessly with Gallabox. Here’s how Gallabox can help:</p>

                <section className="flex md:flex-row flex-col gap-6 mt-9">
                    {paymentGateWayData.map((paymentGate, idx) => {
                        return (
                            <a className="p-5 border border-gray-200 border-solid rounded-md hover:no-underline" key={idx} href={paymentGate.externalLink}>
                                <div className="flex gap-5 mb-9">
                                    <img src={paymentGate.icon} alt="" width={64} height={64} />
                                    <div className="flex flex-col gap-1">
                                        <p>{paymentGate.title}</p>
                                        <p>payment</p>
                                    </div>
                                </div>
                                <p>{paymentGate.descritpion}</p>
                                <GoArrowUpRight size={20} className="mt-5 ml-auto" />
                            </a>
                        );
                    })}
                </section>
            </section>

            <section className="lg:block hidden">
                <PaymentStepsCard title={"How to use Gallabox’s WhatsApp marketing software"} />
            </section>
            <PaymentStepsAccordion />

            <section>
                <FeatureInfoCard {...featureInfo1} />
                <FeatureInfoCard {...featureInfo2} />
                <FeatureInfoCard {...featureInfo3} />
                <FeatureInfoCard {...featureInfo4} />
            </section>
            <ProductSection title="Best practices for using WhatsApp Payment Integration" productCardData={productCardData} />
            <section>
                <CaseStudyFeatureSection features={caseStudyFeatureCardData} backgroundColor={true} />
            </section>

            <FAQSection faqData={defaultFAQData} />

            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Integrate your payment gateway with Gallabox now!" description="Be among the first to ride the WhatsApp automation wave" />
        </NewLayout>
    );
};

export default Integration;
